import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "type", "language", "playlistId", "trackId"];
import { useMemo } from 'react';
import { useCatalogCardModels } from '../../catalog/hooks/useCatalogCardModels';
import { useSimplePlaylists } from '../../hubspotPlaylists/hooks/useSimplePlaylists';
import { useUserProgressForLearningContent } from '../../userProgress/hooks/useUserProgressForLearningContent';
import { useGetEventsQuery } from '../../../../__generated__/chirp/com/hubspot/academy/learning/events/rpc/LearningEventUserRpc.hooks';
import { toLiveEventCardModel } from '../../liveEvents/toLiveEventCardModel';
const isEqualItem = (itemA, itemB) => {
  var _itemA$language, _itemB$language;
  return itemA.id === itemB.id && itemA.type === itemB.type && (itemA.type === 'PLAYLIST' || itemA.type === 'LEARNING_PATH' || itemA.type === 'SHORT_FORM_CONTENT' || itemA.type === 'LIVE_EVENT' || ('language' in itemA ? ((_itemA$language = itemA.language) === null || _itemA$language === void 0 ? void 0 : _itemA$language.toLowerCase()) === ((_itemB$language = itemB.language) === null || _itemB$language === void 0 ? void 0 : _itemB$language.toLowerCase()) : true));
};
export const useLearningContentCardModels = ({
  items,
  skip,
  skipProgress
}) => {
  const playlistIds = items.filter(({
    type
  }) => type === 'LEARNING_PATH' || type === 'PLAYLIST').map(item => item.id);
  const liveEventIds = items.filter(({
    type
  }) => type === 'LIVE_EVENT').map(item => item.id);
  const catalogItems = items.filter(({
    type
  }) => type !== 'LEARNING_PATH' && type !== 'PLAYLIST' && type !== 'LIVE_EVENT');

  // hydrated models for catalog items (tracks, lessons, shorts)
  const {
    catalogCardModels,
    catalogCardModelsLoading,
    catalogCardModelsError,
    apolloError: catalogApolloError
  } = useCatalogCardModels({
    items: catalogItems,
    skip
  });

  // hydrated models for playlists
  const {
    simplePlaylists,
    simplePlaylistsLoading,
    simplePlaylistsError,
    apolloError: simplePlaylistsApolloError
  } = useSimplePlaylists({
    playlistIds,
    skip
  });

  // hydrated models for live events — server filters by eventIds, no FE filter.
  const {
    data: learningEventsData,
    loading: learningEventsLoading,
    error: learningEventsApolloError
  } = useGetEventsQuery({
    variables: {
      ids: liveEventIds
    },
    skip: skip || liveEventIds.length === 0
  });
  const learningEventsById = useMemo(() => {
    var _learningEventsData$r;
    const map = new Map();
    learningEventsData === null || learningEventsData === void 0 || (_learningEventsData$r = learningEventsData.results) === null || _learningEventsData$r === void 0 || _learningEventsData$r.forEach(event => {
      map.set(event.id, toLiveEventCardModel(event));
    });
    return map;
  }, [learningEventsData === null || learningEventsData === void 0 ? void 0 : learningEventsData.results]);

  // user progress data for all items
  const {
    userProgressData,
    userProgressDataLoading,
    userProgressDataError,
    apolloError: userProgressApolloError
  } = useUserProgressForLearningContent({
    items,
    skip: skipProgress || skip
  });

  // merge all data into a single array of card models
  const cardModels = useMemo(() => {
    if (catalogCardModelsLoading || simplePlaylistsLoading || learningEventsLoading) return null;
    const modelsAndProgress = items.map(item => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
          id,
          type
        } = item,
        customProps = _objectWithoutPropertiesLoose(item, _excluded);
      let cardModel;
      if (type === 'LEARNING_PATH' || type === 'PLAYLIST') {
        const playlist = simplePlaylists === null || simplePlaylists === void 0 ? void 0 : simplePlaylists[item.id];
        if (playlist) {
          cardModel = Object.assign({}, playlist, {
            type: playlist.type === 'LEARNING_PATH' ? 'LEARNING_PATH' : 'PLAYLIST'
          });
        }
      } else if (type === 'LIVE_EVENT') {
        cardModel = learningEventsById.get(item.id);
      } else {
        cardModel = catalogCardModels === null || catalogCardModels === void 0 ? void 0 : catalogCardModels.find(m => isEqualItem(m, item));
      }
      let progress;
      if (type === 'LIVE_EVENT') {
        // Live events have no progress concept yet; keep null so consumers
        // can branch the same way they do for missing progress data.
        progress = null;
      } else if (cardModel && userProgressData) {
        progress = userProgressData.find(p => isEqualItem(p, item));
        if (!progress) {
          // if there is no progress found it means the user has not started the item
          progress = {
            id,
            type: type,
            status: 'NOT_STARTED',
            completedPercentage: 0,
            completedLessonIds: []
          };
        }
      } else if (!userProgressDataLoading) progress = null;
      return Object.assign({}, cardModel, {
        progress,
        id: cardModel && item.id
      }, customProps);
    });
    return modelsAndProgress.filter(m => !!m.id);
  }, [catalogCardModels, catalogCardModelsLoading, items, learningEventsById, learningEventsLoading, simplePlaylists, simplePlaylistsLoading, userProgressData, userProgressDataLoading]);
  return {
    cardModels,
    cardModelsLoading: catalogCardModelsLoading || simplePlaylistsLoading || learningEventsLoading,
    cardModelsError: catalogCardModelsError || simplePlaylistsError,
    userProgressDataError,
    userProgressDataLoading,
    apolloError: catalogApolloError || simplePlaylistsApolloError || userProgressApolloError || learningEventsApolloError
  };
};